import { useMemo, type Dispatch, type SetStateAction } from 'react'
import type { Character } from '@/types'
import type { Member as MemberType } from '@/types/member'
import type { ChatMode, MediaFile } from '@/types/message'
import type { ChatContextValue, MediaData, RespondingAsNpc } from '../contexts'

interface UseChatContextValueArgs {
  chatMode: ChatMode
  selectedCharacter: Character | null
  selectedPrivateUser: MemberType | null
  selectedMediaFile: string | null
  selectedMediaData: MediaData | null
  respondingAsNpc: RespondingAsNpc
  isConnected: boolean
  isReconnecting: boolean
  loadingHistory: boolean
  loadingPrivateHistory: boolean
  isMaster: boolean
  setSelectedCharacter: Dispatch<SetStateAction<Character | null>>
  handleSendMessage: (text: string) => boolean
  handleSendPrivateMessage: (text: string) => Promise<boolean>
  handleMediaUpload: (mediaFile: MediaFile) => void
  handleToggleRespondAsNpc: (npc: Character | null) => void
  handleRollDice: () => void
  openLiveSyncControl: () => void
  openPollControl: () => void
  openLabControl: () => void
  enabledModules: string[]
  realTableId: string
  sessaoDaMissao: ChatContextValue['sessaoDaMissao']
}

/**
 * Empacota o estado de chat como ChatContextValue.
 * Extraído de GameSession.tsx para reduzir ruído no orquestrador.
 *
 * Fase 11: o texto digitado vive no ChatInputContainer (estado local) e o
 * envio recebe o texto por argumento — este memo não é mais invalidado a
 * cada keystroke.
 */
export function useChatContextValue(args: UseChatContextValueArgs): ChatContextValue {
  const {
    chatMode,
    selectedCharacter, selectedPrivateUser, selectedMediaFile, selectedMediaData,
    respondingAsNpc, isConnected, isReconnecting, loadingHistory, loadingPrivateHistory, isMaster,
    setSelectedCharacter,
    handleSendMessage, handleSendPrivateMessage, handleMediaUpload,
    handleToggleRespondAsNpc, handleRollDice,
    openLiveSyncControl, openPollControl, openLabControl, enabledModules, realTableId,
    sessaoDaMissao,
  } = args

  return useMemo<ChatContextValue>(() => ({
    chatMode,
    selectedCharacter,
    selectedPrivateUser,
    selectedMediaFile,
    selectedMediaData,
    respondingAsNpc,
    isConnected,
    isReconnecting,
    loadingHistory,
    loadingPrivateHistory,
    isMaster,
    onSendMessage: handleSendMessage,
    onSendPrivateMessage: handleSendPrivateMessage,
    onSelectCharacter: setSelectedCharacter,
    onMediaUpload: handleMediaUpload,
    onToggleRespondAsNpc: handleToggleRespondAsNpc,
    onRollDice: handleRollDice,
    onLiveSync: openLiveSyncControl,
    onPoll: openPollControl,
    onLab: openLabControl,
    enabledModules,
    sessaoDaMissao,
    tableId: realTableId,
  }), [
    sessaoDaMissao,
    chatMode, selectedCharacter, selectedPrivateUser,
    selectedMediaFile, selectedMediaData, respondingAsNpc, isConnected, isReconnecting, loadingHistory,
    loadingPrivateHistory, isMaster, setSelectedCharacter, handleSendMessage,
    handleSendPrivateMessage, handleMediaUpload, handleToggleRespondAsNpc,
    handleRollDice, realTableId, openLiveSyncControl, openPollControl, openLabControl,
    enabledModules,
  ])
}
